import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import OgShell from '../components/OgShell';
import AuthGate from '../components/AuthGate';
import { Field, TextArea } from '../components/fields';
import {
  fetchAllCases,
  fetchAllFollowups,
  fetchConsent,
  fetchFollowups,
  fetchReview,
  fetchSessionRecords,
  confirmQiCaseByPractitioner,
  pauseQiCasePublication,
  publishWitness,
  resolveQiCaseDispute,
  reviewQiCaseDeletion,
  saveReview,
} from '../lib/api';
import { CASE_STATUS_LABELS, CONSENT_FIELD_LABELS, EVIDENCE_LEVELS, FOLLOWUP_LABELS, PUBLICATION_WITHDRAWAL_REASONS, RECIPIENT_CONSENT_ITEMS, buildAnonymousCaseSummary, evaluatePublishEligibility, formatDate } from '../lib/constants';
import type { QiCase, QiConsent, QiFollowup, QiReview, QiSessionRecord } from '../lib/types';

// qi_cases 上的治理欄位（fetchAllCases 用 select('*') 本來就抓得到，此型別只是補齊型別供 Admin 區塊使用）。
type GovernedCase = QiCase & {
  deletion_status: string | null;
  deletion_reason_category: string | null;
  deletion_reason: string | null;
  deletion_requested_by: string | null;
  deletion_requested_at: string | null;
  deletion_review_note: string | null;
  pre_deletion_status: string | null;
  publication_paused_at: string | null;
  disputed: boolean;
  disputed_at: string | null;
  disputed_reason: string | null;
};

export default function ReviewPage() {
  return (
    <OgShell>
      <div className="max-w-5xl mx-auto px-4 md:px-6">
        <AuthGate requireStaff>
          {(auth) => <ReviewDashboard reviewerId={auth.userId} role={auth.role} />}
        </AuthGate>
      </div>
    </OgShell>
  );
}

function ReviewDashboard({ reviewerId, role }: { reviewerId: string; role: string }) {
  const [cases, setCases] = useState<QiCase[]>([]);
  const [followups, setFollowups] = useState<QiFollowup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [search, setSearch] = useState('');

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detailRecords, setDetailRecords] = useState<QiSessionRecord[]>([]);
  const [detailFollowups, setDetailFollowups] = useState<QiFollowup[]>([]);
  const [detailReview, setDetailReview] = useState<QiReview | null>(null);
  const [detailConsent, setDetailConsent] = useState<QiConsent | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [requestSupplement, setRequestSupplement] = useState(false);
  const [evidenceLevel, setEvidenceLevel] = useState('E1');
  const [publicSummary, setPublicSummary] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [pauseReason, setPauseReason] = useState('');
  const [pauseReasonOther, setPauseReasonOther] = useState('');

  // ── Admin 刪除審核 / 爭議處理狀態 ──
  const [deletionTarget, setDeletionTarget] = useState<GovernedCase | null>(null);
  const [deletionAction, setDeletionAction] = useState<'archive' | 'reject' | null>(null);
  const [deletionNote, setDeletionNote] = useState('');
  const [disputeTarget, setDisputeTarget] = useState<GovernedCase | null>(null);
  const [disputeNote, setDisputeNote] = useState('');
  const [adminBusy, setAdminBusy] = useState(false);
  const [adminMsg, setAdminMsg] = useState<string | null>(null);

  const load = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    setError(null);
    try {
      const [c, f] = await Promise.all([fetchAllCases(), fetchAllFollowups()]);
      setCases(c);
      setFollowups(f);
    } catch (err) {
      setError(err instanceof Error ? err.message : '載入失敗');
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCase = useCallback(async (c: QiCase) => {
    setSelectedId(c.id);
    setDetailLoading(true);
    setActionMsg(null);
    try {
      const [r, f, rev, con] = await Promise.all([
        fetchSessionRecords(c.id),
        fetchFollowups(c.id),
        fetchReview(c.id),
        fetchConsent(c.id),
      ]);
      setDetailRecords(r);
      setDetailFollowups(f);
      setDetailReview(rev);
      setDetailConsent(con);
      if (rev) {
        setEvidenceLevel(rev.evidence_level || 'E1');
        setPublicSummary(rev.public_summary || buildAnonymousCaseSummary(c, r, f));
        setInternalNotes(rev.internal_notes || '');
        setRequestSupplement(rev.review_status === 'need_more_evidence');
      } else {
        setEvidenceLevel('E1');
        setPublicSummary(buildAnonymousCaseSummary(c, r, f));
        setInternalNotes('');
        setRequestSupplement(false);
      }
    } catch {
      setActionMsg('載入案例詳情失敗');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handlePublish = useCallback(async () => {
    if (!selectedId) return;
    setSaving(true);
    setActionMsg(null);
    try {
      const selectedCase = cases.find((item) => item.id === selectedId);
      const summary = publicSummary.trim() || (selectedCase
        ? buildAnonymousCaseSummary(selectedCase, detailRecords, detailFollowups)
        : '本案例已完成宇炁實作紀錄，相關內容與後續變化持續保存及追蹤。');
      setPublicSummary(summary);
      await confirmQiCaseByPractitioner(selectedId);
      await saveReview(selectedId, reviewerId, {
        review_status: 'approved',
        evidence_level: evidenceLevel,
        public_summary: summary,
        internal_notes: internalNotes,
      });
      const result = await publishWitness(
        selectedId,
        '匿名見證',
        summary,
        evidenceLevel,
        requestSupplement,
      );
      const published = result.action === 'publish' ? '已發布到匿名見證牆。' : '已送交 ABAA 覆核，尚未公開。';
      setActionMsg(result.noticeWarning ? `${published} ${result.noticeWarning}。` : published);
      await load(false);
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : '發布失敗');
    } finally {
      setSaving(false);
    }
  }, [selectedId, reviewerId, publicSummary, evidenceLevel, internalNotes, requestSupplement, cases, detailRecords, detailFollowups, load]);

  const handlePausePublication = useCallback(async () => {
    if (!selectedId) return;
    if (!pauseReason) {
      setActionMsg('撤下公開時必須選擇理由。');
      return;
    }
    setSaving(true);
    setActionMsg(null);
    try {
      await pauseQiCasePublication(selectedId, pauseReason, pauseReasonOther);
      setActionMsg('已停止公開並保存撤下理由；原始紀錄與歷史版本未刪除。');
      await load(false);
    } catch (err) {
      setActionMsg(err instanceof Error ? err.message : '撤下失敗');
    } finally {
      setSaving(false);
    }
  }, [selectedId, pauseReason, pauseReasonOther, load]);

  // ── Admin 治理：清單與處理函式 ──
  const governedCases = cases as GovernedCase[];
  const pendingDeletions = governedCases.filter((c) => c.deletion_status === 'deletion_requested');
  const pendingDisputes = governedCases.filter((c) => c.disputed === true);

  const openDeletionReview = useCallback((c: GovernedCase, action: 'archive' | 'reject') => {
    setDeletionTarget(c);
    setDeletionAction(action);
    setDeletionNote('');
    setAdminMsg(null);
  }, []);

  const confirmDeletionReview = useCallback(async () => {
    if (!deletionTarget || !deletionAction) return;
    if (!deletionNote.trim()) {
      setAdminMsg('請填寫審核備註。');
      return;
    }
    setAdminBusy(true);
    setAdminMsg(null);
    try {
      const res = await reviewQiCaseDeletion(
        deletionTarget.id,
        deletionAction,
        deletionNote.trim(),
        deletionTarget.case_code || '',
      );
      if (!res.ok) {
        setAdminMsg(res.error || '審核失敗');
        return;
      }
      setAdminMsg(deletionAction === 'archive' ? '已核准歸檔，案例退出公開見證牆。' : '已駁回刪除申請，恢復申請前狀態。');
      setDeletionTarget(null);
      setDeletionAction(null);
      setDeletionNote('');
      await load();
    } catch (err) {
      setAdminMsg(err instanceof Error ? err.message : '審核失敗');
    } finally {
      setAdminBusy(false);
    }
  }, [deletionTarget, deletionAction, deletionNote, load]);

  const openDisputeResolve = useCallback((c: GovernedCase) => {
    setDisputeTarget(c);
    setDisputeNote('');
    setAdminMsg(null);
  }, []);

  const confirmDisputeResolve = useCallback(async () => {
    if (!disputeTarget) return;
    if (!disputeNote.trim()) {
      setAdminMsg('請填寫處理備註。');
      return;
    }
    setAdminBusy(true);
    setAdminMsg(null);
    try {
      const res = await resolveQiCaseDispute(disputeTarget.id, disputeNote.trim());
      if (!res.ok) {
        setAdminMsg(res.error || '處理失敗');
        return;
      }
      setAdminMsg('已標記爭議解決，公開狀態依後端規則更新。');
      setDisputeTarget(null);
      setDisputeNote('');
      await load();
    } catch (err) {
      setAdminMsg(err instanceof Error ? err.message : '處理失敗');
    } finally {
      setAdminBusy(false);
    }
  }, [disputeTarget, disputeNote, load]);

  // 「維持爭議」目前沒有對應 RPC（resolve_qi_case_dispute 只能解決，不能記錄維持備註）。
  // 因此僅顯示現況，不做假性後端寫入。
  const maintainDispute = useCallback((c: GovernedCase) => {
    setAdminMsg(`案例 ${c.case_code || `#${c.id}`} 維持爭議，公開維持暫停（無後端變更）。`);
  }, []);

  const today = new Date().toDateString();
  const todayNew = cases.filter((c) => new Date(c.created_at).toDateString() === today).length;
  const awaitingRecipient = cases.filter((c) => c.status === 'awaiting_recipient').length;
  const awaitingReview = cases.filter((c) => c.status === 'submitted' || c.status === 'under_review').length;
  const pendingFollowups = followups.filter((f) => f.status !== 'completed').length;
  const overdue = followups.filter((f) => f.status !== 'completed' && f.due_date && new Date(f.due_date) < new Date()).length;
  const approvedAnonymous = cases.filter((c) => c.status === 'approved_anonymous' || c.status === 'published').length;

  const filtered = cases.filter((c) => {
    if (statusFilter && c.status !== statusFilter) return false;
    if (levelFilter && c.evidence_level !== levelFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = `${c.case_code || ''} ${c.recipient_alias || ''} ${c.location || ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const reviewedStatuses = new Set(['approved_anonymous', 'published', 'withdrawn', 'archived', 'private_complete']);
  const sortedFiltered = [
    ...filtered.filter((c) => !reviewedStatuses.has(c.status)),
    ...filtered.filter((c) => reviewedStatuses.has(c.status)),
  ];

  const eligibility = evaluatePublishEligibility(detailConsent);

  if (loading) {
    return (
      <div className="py-20 text-center">
        <div className="w-10 h-10 mx-auto border-2 border-accent-300 border-t-accent-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-16 text-center space-y-3">
        <p className="text-sm text-rose-600">{error}</p>
        <button onClick={() => load()} className="whitespace-nowrap px-4 py-2 rounded-md border border-foreground-200 text-sm cursor-pointer">重試</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1 pt-2">
        <h1 className="text-xl md:text-2xl text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
          ABAA 覆核後台
        </h1>
        <p className="text-sm text-foreground-500">查看、篩選與覆核宇炁療育案例。AI 不自行核定，Evidence 等級由 Reviewer 人工確認。</p>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <StatCard label="總案例數" value={cases.length} />
        <StatCard label="今日新案例" value={todayNew} />
        <StatCard label="等待受施者確認" value={awaitingRecipient} />
        <StatCard label="等待覆核" value={awaitingReview} />
        <StatCard label="待追蹤" value={pendingFollowups} />
        <StatCard label="已逾期" value={overdue} accent />
        <StatCard label="已核准匿名公開" value={approvedAnonymous} />
        <StatCard label="需要注意的反應" value={cases.filter((c) => c.status === 'need_more_evidence').length} />
      </div>

      {/* Admin 刪除審核與爭議處理（僅系統管理員可見） */}
      {role === 'admin' && (
        <div className="space-y-4">
          <AdminDeletionSection pending={pendingDeletions} onReview={openDeletionReview} />
          <AdminDisputeSection pending={pendingDisputes} onResolve={openDisputeResolve} onMaintain={maintainDispute} />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="搜尋編號／稱呼／地點…"
          className="px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-accent-300"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-accent-300"
        >
          <option value="">全部狀態</option>
          {Object.entries(CASE_STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value)}
          className="px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-accent-300"
        >
          <option value="">全部 Evidence 等級</option>
          {EVIDENCE_LEVELS.map((l) => (
            <option key={l.value} value={l.value}>{l.label}</option>
          ))}
        </select>
      </div>

      {/* List */}
      <div className="space-y-2">
        {sortedFiltered.length === 0 ? (
          <p className="text-sm text-foreground-400 bg-background-100 border border-foreground-200/50 rounded-xl p-6 text-center">沒有符合條件的案例。</p>
        ) : (
          sortedFiltered.map((c, index) => (
            <div key={c.id} className="space-y-2">
              {reviewedStatuses.has(c.status) && (index === 0 || !reviewedStatuses.has(sortedFiltered[index - 1].status)) && (
                <div className="pt-5 pb-1">
                  <h2 className="text-sm font-medium text-foreground-700">已覆核／已發布案例</h2>
                  <p className="mt-1 text-xs text-foreground-400">保留於此供日後查找、再次檢視、停止公開或退出見證牆。</p>
                </div>
              )}
              <div className="bg-background-100 border border-foreground-200/50 rounded-xl overflow-hidden">
              <button
                onClick={() => (selectedId === c.id ? setSelectedId(null) : openCase(c))}
                className="w-full flex items-center justify-between gap-3 p-4 text-left cursor-pointer hover:bg-background-200/60 transition-colors"
              >
                <div>
                  <p className="text-sm font-medium text-foreground-800">{c.case_code || `案例 #${c.id}`}</p>
                  <p className="text-xs text-foreground-400 mt-0.5">
                    {c.recipient_alias || '未填稱呼'} &middot; {formatDate(c.session_datetime)} &middot; {c.location || '未填地點'}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap">
                    {c.evidence_level}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-background-200 text-foreground-600 whitespace-nowrap">
                    {CASE_STATUS_LABELS[c.status] || c.status}
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs text-accent-700 whitespace-nowrap">
                    {selectedId === c.id ? '收合摘要' : '展開摘要'}
                    <i className={selectedId === c.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
                  </span>
                </div>
              </button>

              {selectedId === c.id && (
                <div className="border-t border-foreground-200/50 p-4 space-y-4">
                  {detailLoading ? (
                    <div className="py-6 text-center">
                      <div className="w-7 h-7 mx-auto border-2 border-accent-300 border-t-accent-500 rounded-full animate-spin" />
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-accent-200 bg-accent-50/70 px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-foreground-800">案例摘要已展開</p>
                          <p className="mt-0.5 text-xs text-foreground-500">進入完整案例可查看全部登錄欄位、照片／文件、追蹤與授權紀錄。</p>
                        </div>
                        <Link
                          to={`/og/qi-healing/case/${c.id}`}
                          className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-accent-600"
                        >
                          查看完整案例
                          <i className="ri-arrow-right-line" />
                        </Link>
                      </div>

                      <div className="space-y-2">
                        {detailRecords.map((r) => (
                          <div key={r.id} className="text-xs text-foreground-500">
                            <span className="text-foreground-400">{r.record_stage}：</span>
                            {r.narrative || '（無敘述）'}
                          </div>
                        ))}
                        {detailFollowups.map((f) => (
                          <div key={f.id} className="text-xs text-foreground-500">
                            <span className="text-foreground-400">{FOLLOWUP_LABELS[f.followup_type] || f.followup_type}：</span>
                            {f.status === 'completed' ? '已完成' : `待追蹤（${f.due_date || '未定'}）`}
                            {f.self_report ? ` — ${f.self_report.slice(0, 60)}` : ''}
                          </div>
                        ))}
                      </div>

                      <div className="grid grid-cols-1 gap-4">
                        <Field label="Evidence 等級">
                          <select
                            value={evidenceLevel}
                            onChange={(e) => setEvidenceLevel(e.target.value)}
                            className="w-full px-3 py-2.5 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-accent-300"
                          >
                            {EVIDENCE_LEVELS.map((l) => (
                              <option key={l.value} value={l.value}>{l.label}</option>
                            ))}
                          </select>
                        </Field>
                      </div>

                      <Field label="公開摘要（將顯示於匿名見證牆）">
                        <TextArea value={publicSummary} onChange={setPublicSummary} rows={3} maxLength={500} />
                      </Field>
                      <Field label="內部備註（不對當事人顯示）">
                        <TextArea value={internalNotes} onChange={setInternalNotes} rows={2} maxLength={500} />
                      </Field>

                      <label className="flex items-start gap-2 rounded-lg border border-secondary-200 bg-secondary-50 px-3 py-2 text-xs text-secondary-900">
                        <input
                          type="checkbox"
                          checked={requestSupplement}
                          onChange={(event) => setRequestSupplement(event.target.checked)}
                          className="mt-0.5"
                        />
                        <span>發布後寄信給施炁者與案主，告知仍可補充或更正（不延後發布）。</span>
                      </label>

                      <div className="rounded-xl border border-foreground-200/60 bg-background-50 p-4 space-y-3">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="text-sm font-medium text-foreground-800">公開與媒體權限</h4>
                          <span className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap ${eligibility.canPublishText ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                            {eligibility.canPublishText ? '匿名文字預設可送審' : '案主已撤回公開'}
                          </span>
                        </div>

                        <p className="text-xs text-foreground-600">
                          施炁者完成去識別確認後，保存、ABAA／Reviewer 查看、內部研究與匿名文字公開皆預設通過；只有露臉照片或影片須由案主逐檔同意。
                        </p>

                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {RECIPIENT_CONSENT_ITEMS.map((item) => {
                            const requiresRecipientChoice = item.key === 'media_public_use';
                            const val = requiresRecipientChoice
                              ? detailConsent?.media_public_use === true
                              : eligibility.canPublishText;
                            return (
                              <li key={item.key} className="flex items-start gap-2 text-xs">
                                <i className={`mt-0.5 ${val ? 'ri-checkbox-circle-fill text-emerald-500' : 'ri-checkbox-blank-circle-line text-foreground-300'}`} />
                                <span className={val ? 'text-foreground-700' : 'text-foreground-400'}>{item.label}</span>
                              </li>
                            );
                          })}
                        </ul>

                        <div className="text-xs text-foreground-400 space-y-1 border-t border-foreground-200/50 pt-2">
                          <p>同意版本：{detailConsent?.consent_version || '—'}</p>
                          <p>同意時間：{detailConsent?.consented_at ? formatDate(detailConsent.consented_at) : '—'}</p>
                          <p>撤回時間：{detailConsent?.withdrawn_at ? formatDate(detailConsent.withdrawn_at) : '未撤回'}</p>
                        </div>

                        {eligibility.missing.length > 0 && (
                          <p className="text-xs text-rose-600">
                            缺少授權：{eligibility.missing.map((k) => CONSENT_FIELD_LABELS[k] || k).join('、')}
                          </p>
                        )}

                        {eligibility.canPublishText && !eligibility.canPublishMedia && (
                          <div className="text-xs bg-secondary-100 text-secondary-900 rounded-md px-3 py-2">
                            <i className="ri-information-line mr-1" />
                            匿名文字及無臉資料可公開；露臉照片／影片尚未取得案主逐檔公開同意。
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 gap-4">
                        <button
                          onClick={handlePublish}
                          disabled={saving}
                          className="whitespace-nowrap px-4 py-2 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {saving ? '處理中…' : '核准並直接發布到見證牆'}
                        </button>
                      </div>
                      {actionMsg && (
                        <p className={`rounded-md px-3 py-2 text-sm ${actionMsg.includes('已發布') ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-700'}`}>
                          {actionMsg}
                        </p>
                      )}
                      <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-4 space-y-3">
                        <h4 className="text-sm font-medium text-rose-900">事後撤下公開</h4>
                        <select
                          value={pauseReason}
                          onChange={(event) => setPauseReason(event.target.value)}
                          className="w-full px-3 py-2 rounded-md border border-rose-200 bg-white text-sm"
                        >
                          <option value="">請選擇撤下理由（必選）</option>
                          {PUBLICATION_WITHDRAWAL_REASONS.map((item) => (
                            <option key={item.value} value={item.value}>{item.label}</option>
                          ))}
                        </select>
                        <TextArea
                          value={pauseReasonOther}
                          onChange={setPauseReasonOther}
                          rows={2}
                          maxLength={500}
                          placeholder="其他說明（選填）"
                        />
                        <button
                          type="button"
                          onClick={handlePausePublication}
                          disabled={saving || !pauseReason}
                          className="px-4 py-2 rounded-md border border-rose-300 text-rose-700 text-sm disabled:opacity-50"
                        >
                          停止公開
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Admin 刪除審核確認視窗 */}
      {deletionTarget && deletionAction && (
        <DeletionReviewModal
          target={deletionTarget}
          action={deletionAction}
          note={deletionNote}
          setNote={setDeletionNote}
          busy={adminBusy}
          msg={adminMsg}
          onCancel={() => {
            setDeletionTarget(null);
            setDeletionAction(null);
            setDeletionNote('');
            setAdminMsg(null);
          }}
          onConfirm={confirmDeletionReview}
        />
      )}

      {/* Admin 爭議解決確認視窗 */}
      {disputeTarget && (
        <DisputeResolveModal
          target={disputeTarget}
          note={disputeNote}
          setNote={setDisputeNote}
          busy={adminBusy}
          msg={adminMsg}
          onCancel={() => {
            setDisputeTarget(null);
            setDisputeNote('');
            setAdminMsg(null);
          }}
          onConfirm={confirmDisputeResolve}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, accent = false }: { label: string; value: number; accent?: boolean }) {
  return (
    <div className={`rounded-xl p-4 border ${accent ? 'bg-accent-100/70 border-accent-200' : 'bg-background-100 border-foreground-200/50'}`}>
      <p className={`text-2xl font-semibold ${accent ? 'text-accent-800' : 'text-foreground-800'}`}>{value}</p>
      <p className="text-xs text-foreground-500 mt-1">{label}</p>
    </div>
  );
}

function shortId(id: string | null | undefined): string {
  if (!id) return '—';
  return id.length > 8 ? `${id.slice(0, 8)}…` : id;
}

function ModalShell({ title, onCancel, children }: { title: string; onCancel: () => void; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onCancel}>
      <div
        className="w-full max-w-md bg-background-50 border border-foreground-200 rounded-xl p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-foreground-900">{title}</h3>
          <button
            onClick={onCancel}
            className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-700 cursor-pointer"
            aria-label="關閉"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function AdminDeletionSection({
  pending,
  onReview,
}: {
  pending: GovernedCase[];
  onReview: (c: GovernedCase, action: 'archive' | 'reject') => void;
}) {
  return (
    <section className="rounded-xl border border-foreground-200/60 bg-background-100 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground-900">待審核刪除申請</h2>
        <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap">{pending.length} 筆</span>
      </div>
      {pending.length === 0 ? (
        <p className="text-sm text-foreground-400 text-center py-4">目前沒有待審核的刪除申請。</p>
      ) : (
        <div className="space-y-2">
          {pending.map((c) => (
            <div key={c.id} className="rounded-lg border border-foreground-200/50 bg-background-50 p-3 space-y-2">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div>
                  <p className="text-sm font-medium text-foreground-800">{c.case_code || `案例 #${c.id}`}</p>
                  <p className="text-xs text-foreground-500 mt-0.5">
                    施炁者：{c.practitioner_name_snapshot || shortId(c.practitioner_id)} &middot; 受施者：{c.recipient_alias || '匿名'}
                  </p>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full bg-background-200 text-foreground-600 whitespace-nowrap">{CASE_STATUS_LABELS[c.status] || c.status}</span>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-foreground-500">
                <div>申請人：<span className="text-foreground-700">{shortId(c.deletion_requested_by)}</span></div>
                <div>申請時間：{formatDate(c.deletion_requested_at)}</div>
                <div>理由類別：{c.deletion_reason_category || '—'}</div>
                <div>原本公開：{c.pre_deletion_status === 'published' ? '是' : '否'}</div>
                <div className="sm:col-span-2">補充說明：{c.deletion_reason || '—'}</div>
                <div className="sm:col-span-2">公開暫停：{c.publication_paused_at ? `是（${formatDate(c.publication_paused_at)}）` : '否'}</div>
              </dl>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => onReview(c, 'archive')}
                  className="whitespace-nowrap px-3 py-1.5 rounded-md bg-accent-500 text-background-50 text-xs hover:bg-accent-600 transition-colors cursor-pointer"
                >
                  核准歸檔
                </button>
                <button
                  onClick={() => onReview(c, 'reject')}
                  className="whitespace-nowrap px-3 py-1.5 rounded-md bg-foreground-100 text-foreground-700 text-xs hover:bg-foreground-200 transition-colors cursor-pointer"
                >
                  駁回刪除申請
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function AdminDisputeSection({
  pending,
  onResolve,
  onMaintain,
}: {
  pending: GovernedCase[];
  onResolve: (c: GovernedCase) => void;
  onMaintain: (c: GovernedCase) => void;
}) {
  return (
    <section className="rounded-xl border border-foreground-200/60 bg-background-100 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-foreground-900">待處理爭議</h2>
        <span className="text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap">{pending.length} 筆</span>
      </div>
      {pending.length === 0 ? (
        <p className="text-sm text-foreground-400 text-center py-4">目前沒有待處理的爭議。</p>
      ) : (
        <div className="space-y-2">
          {pending.map((c) => (
            <div key={c.id} className="rounded-lg border border-foreground-200/50 bg-background-50 p-3 space-y-2">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <p className="text-sm font-medium text-foreground-800">{c.case_code || `案例 #${c.id}`}</p>
                <span className="text-xs px-2 py-0.5 rounded-full bg-background-200 text-foreground-600 whitespace-nowrap">{CASE_STATUS_LABELS[c.status] || c.status}</span>
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-foreground-500">
                <div>提出時間：{formatDate(c.disputed_at)}</div>
                <div>公開暫停：{c.publication_paused_at ? `是（${formatDate(c.publication_paused_at)}）` : '否'}</div>
                <div className="sm:col-span-2">爭議原因：{c.disputed_reason || '—'}</div>
              </dl>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => onResolve(c)}
                  className="whitespace-nowrap px-3 py-1.5 rounded-md bg-accent-500 text-background-50 text-xs hover:bg-accent-600 transition-colors cursor-pointer"
                >
                  標記爭議已解決
                </button>
                <button
                  onClick={() => onMaintain(c)}
                  className="whitespace-nowrap px-3 py-1.5 rounded-md bg-foreground-100 text-foreground-700 text-xs hover:bg-foreground-200 transition-colors cursor-pointer"
                >
                  維持爭議／繼續暫停
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function DeletionReviewModal({
  target,
  action,
  note,
  setNote,
  busy,
  msg,
  onCancel,
  onConfirm,
}: {
  target: GovernedCase;
  action: 'archive' | 'reject';
  note: string;
  setNote: (v: string) => void;
  busy: boolean;
  msg: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const isArchive = action === 'archive';
  return (
    <ModalShell title={isArchive ? '核准歸檔確認' : '駁回刪除申請確認'} onCancel={onCancel}>
      <div className="space-y-3 text-xs text-foreground-600">
        <p>
          案例編號：<span className="text-foreground-800">{target.case_code || `#${target.id}`}</span>
        </p>
        <p className="rounded-md bg-background-100 border border-foreground-200/50 px-3 py-2">
          {isArchive
            ? '歸檔不是物理刪除：案例資料、媒體、修訂紀錄與治理 Log 都會保留，案例將退出公開見證牆。'
            : '駁回後將恢復申請前狀態；若原本已公開且無其他暫停原因，將依後端規則恢復公開資格。'}
        </p>
        <div>
          <label className="block text-xs font-medium text-foreground-700 mb-1">審核備註（必填）</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={500}
            className="w-full px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-accent-300"
          />
        </div>
        {msg && <p className="text-xs text-rose-600">{msg}</p>}
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            disabled={busy}
            className="whitespace-nowrap px-3 py-1.5 rounded-md bg-foreground-100 text-foreground-700 text-xs hover:bg-foreground-200 transition-colors cursor-pointer disabled:opacity-50"
          >
            取消
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="whitespace-nowrap px-3 py-1.5 rounded-md bg-accent-500 text-background-50 text-xs hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-50"
          >
            {busy ? '處理中…' : isArchive ? '確認歸檔' : '確認駁回'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

function DisputeResolveModal({
  target,
  note,
  setNote,
  busy,
  msg,
  onCancel,
  onConfirm,
}: {
  target: GovernedCase;
  note: string;
  setNote: (v: string) => void;
  busy: boolean;
  msg: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <ModalShell title="標記爭議已解決確認" onCancel={onCancel}>
      <div className="space-y-3 text-xs text-foreground-600">
        <p>
          案例編號：<span className="text-foreground-800">{target.case_code || `#${target.id}`}</span>
        </p>
        <p className="rounded-md bg-background-100 border border-foreground-200/50 px-3 py-2">
          是否恢復公開完全依後端 RPC 既有規則；前端不自行將案例設為公開。
        </p>
        <div>
          <label className="block text-xs font-medium text-foreground-700 mb-1">處理備註（必填）</label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={500}
            className="w-full px-3 py-2 rounded-md border border-foreground-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-accent-300"
          />
        </div>
        {msg && <p className="text-xs text-rose-600">{msg}</p>}
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            disabled={busy}
            className="whitespace-nowrap px-3 py-1.5 rounded-md bg-foreground-100 text-foreground-700 text-xs hover:bg-foreground-200 transition-colors cursor-pointer disabled:opacity-50"
          >
            取消
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="whitespace-nowrap px-3 py-1.5 rounded-md bg-accent-500 text-background-50 text-xs hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-50"
          >
            {busy ? '處理中…' : '確認解決'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}
